import type { CookingLogEntry, CookingLogWriteInput } from '@recipes/contracts';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  StarRating,
  Textarea,
} from '@recipes/ui';
import { format } from 'date-fns';
import { de as deLocale, enUS } from 'date-fns/locale';
import { CalendarCheck } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/components/feedback/toaster';
import { ImageUploader, type UploaderImage } from '@/components/media/image-uploader';
import { toastError } from '@/lib/errors';
import { currentLocale } from '@/lib/i18n';
import { useAddCookingLog } from './queries';
import { ServingsStepper } from './servings-stepper';

export interface AddCookingLogDialogProps {
  recipeId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultServings: string;
  servingsLabel?: string | null;
  defaultNotes?: string;
  onSaved?: (entry: CookingLogEntry | null) => void;
}

export function AddCookingLogDialog({
  recipeId,
  open,
  onOpenChange,
  defaultServings,
  servingsLabel,
  defaultNotes,
  onSaved,
}: AddCookingLogDialogProps) {
  const { t } = useTranslation();
  const add = useAddCookingLog();
  const [cookedAt, setCookedAt] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [servings, setServings] = useState(defaultServings);
  const [rating, setRating] = useState<number | null>(null);
  const [notes, setNotes] = useState(defaultNotes ?? '');
  const [photo, setPhoto] = useState<UploaderImage[]>([]);
  const [updateRating, setUpdateRating] = useState(true);

  useEffect(() => {
    if (open) {
      setServings(defaultServings);
      setNotes(defaultNotes ?? '');
      setRating(null);
      setPhoto([]);
      setCookedAt(format(new Date(), 'yyyy-MM-dd'));
    }
  }, [open, defaultServings, defaultNotes]);

  const submit = () => {
    const body: CookingLogWriteInput = {
      cookedAt: new Date(`${cookedAt}T12:00:00`).toISOString(),
      servings,
      rating,
      notes: notes.trim() || null,
      photoAssetId: photo[0]?.assetId ?? null,
      updateRecipeRating: rating !== null && updateRating,
    };
    add.mutate(
      { id: recipeId, body },
      {
        onSuccess: (entry) => {
          toast.success(entry ? t('recipes.cookingLog.saved') : t('recipes.cookingLog.queued'));
          onSaved?.(entry);
          onOpenChange(false);
        },
        onError: (e) => toastError(e),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        fullscreenOnMobile
        closeLabel={t('common.close')}
        data-testid="cooking-log-dialog"
      >
        <DialogHeader>
          <DialogTitle>{t('recipes.cookingLog.addTitle')}</DialogTitle>
          <DialogDescription>{t('recipes.cookingLog.addDescription')}</DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <div>
            <Label htmlFor="log-date" className="mb-1 block">
              {t('recipes.cookingLog.date')}
            </Label>
            <Input
              id="log-date"
              type="date"
              value={cookedAt}
              max={format(new Date(), 'yyyy-MM-dd')}
              onChange={(e) => setCookedAt(e.target.value)}
              required
            />
          </div>
          <div>
            <Label className="mb-1 block">{t('recipes.cookingLog.servings')}</Label>
            <ServingsStepper value={servings} onChange={setServings} label={servingsLabel} />
          </div>
          <div>
            <Label className="mb-1 block">{t('recipes.cookingLog.rating')}</Label>
            <StarRating
              value={rating}
              onChange={setRating}
              label={t('recipes.cookingLog.rating')}
            />
            {rating !== null && (
              <label className="mt-1 flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={updateRating}
                  onChange={(e) => setUpdateRating(e.target.checked)}
                  className="size-4"
                />
                {t('recipes.cookingLog.updateRecipeRating')}
              </label>
            )}
          </div>
          <div>
            <Label htmlFor="log-notes" className="mb-1 block">
              {t('recipes.cookingLog.notes')}
            </Label>
            <Textarea
              id="log-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={5000}
              rows={3}
              data-testid="cooking-log-notes"
            />
          </div>
          <div>
            <Label className="mb-1 block">{t('recipes.cookingLog.photo')}</Label>
            <ImageUploader
              purpose="COOKING_LOG"
              value={photo}
              onChange={setPhoto}
              allowAltText={false}
              allowEdit={false}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={add.isPending} data-testid="cooking-log-save">
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function CookingLogList({ entries }: { entries: CookingLogEntry[] }) {
  const { t } = useTranslation();
  const locale = currentLocale() === 'de' ? deLocale : enUS;
  if (entries.length === 0)
    return <p className="text-sm text-muted-foreground">{t('recipes.cookingLog.empty')}</p>;
  return (
    <ul className="divide-y rounded-lg border" data-testid="cooking-log-list">
      {entries.map((entry) => (
        <li key={entry.id} className="flex gap-3 p-3">
          {entry.photoUrl ? (
            <img src={entry.photoUrl} alt="" className="size-14 shrink-0 rounded object-cover" />
          ) : (
            <div className="flex size-14 shrink-0 items-center justify-center rounded bg-muted text-muted-foreground">
              <CalendarCheck className="size-5" aria-hidden />
            </div>
          )}
          <div className="min-w-0 flex-1 text-sm">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <time dateTime={entry.cookedAt} className="font-medium">
                {format(new Date(entry.cookedAt), 'PPP', { locale })}
              </time>
              {entry.servings && (
                <span className="text-muted-foreground">
                  {t('recipes.cookingLog.servingsValue', { count: Number(entry.servings) })}
                </span>
              )}
              {entry.rating !== null && (
                <StarRating
                  value={entry.rating}
                  readOnly
                  size="sm"
                  label={t('recipes.cookingLog.rating')}
                />
              )}
            </div>
            {entry.notes && (
              <p className="mt-1 whitespace-pre-wrap text-muted-foreground">{entry.notes}</p>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}
